"use client"
import { type ComponentProps, type ReactNode, useCallback } from "react"
import Link from "next/link"

/**
 * A wrapper around the Next.js Link component for internal and external links.
 */

type AnchorProps = Omit<ComponentProps<typeof Link>, "href"> & {
    href: string
    children?: ReactNode
    onClick?: () => void
    /** If true, scrolls to top of page on navigation (default: true for internal links) */
    scrollToTop?: boolean
}

export default function Anchor(props: AnchorProps): ReactNode {
    const { scrollToTop = true, onClick, ...restProps } = props
    
    const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.()
        // Scroll to top on internal navigation
        if (scrollToTop && props.href?.startsWith("/")) {
            // Small delay to let navigation complete
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "instant" })
            }, 0)
        }
    }, [onClick, scrollToTop, props.href])

    // No link could be built (e.g. Instagram.instagramPostUrl returned null): render the element without an href rather
    // than throwing, which took down whole server-rendered pages (/instagram/<username>, 1 Nov 2026)
    if (!props.href) {
        const { prefetch, href, ...linklessProps } = restProps as typeof restProps & { href?: string }
        return <a onClick={handleClick} {...linklessProps} />
    }

    if (props.href.startsWith("/"))
        return (
            <Link
                href={props.href}
                passHref
                onClick={handleClick}
                {...restProps}
            />
        )

    const { prefetch, ...otherProps } = restProps
    return <a href={props.href} onClick={handleClick} {...otherProps} />
}
